import { Component, Input, forwardRef, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';

import { I18nKeys } from '../../../i18n';

const INPUT_CONTROL_ACCESSOR = {
	provide: NG_VALUE_ACCESSOR,
	useExisting: forwardRef(() => CheckButtonComponent),
	multi: true
};

/**
 * CheckButtonComponent
 *
 * The CheckButtonComponent is a custom form control that acts like a checkbox but looks like a button.
 * It implements ControlValueAccessor to integrate with Angular forms.
 *
 * It implements several aria attributes to make it accessible for screen readers
 */
@Component({
	selector: 'cjm-check-button',
	providers: [INPUT_CONTROL_ACCESSOR],
	templateUrl: './check-button.component.html',
	styleUrls: ['./check-button.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [TranslateModule]
})
export class CheckButtonComponent implements ControlValueAccessor {
	/** Unique identifier for the checkbox button */
	@Input() public id: string;
	/** Title & aria-label for the checkbox button */
	@Input() public title: string;
	/** Flag to indicate if the projected button content contains an icon */
	@Input() public withIcon: boolean = false;

	public disabled: boolean = false;
	public isChecked: boolean = false;

	public readonly i18nKeys: typeof I18nKeys = I18nKeys;

	private onTouch: Function = () => {};
	private onModelChange: Function = (_: any) => {};

	constructor(private readonly cdRef: ChangeDetectorRef) {}

	/**
	 * writeValue
	 *
	 * Writes a new value to the element.
	 *
	 * @param value - The new value for the checkbox button
	 */
	public writeValue(value: boolean): void {
		this.isChecked = value;

		this.cdRef.markForCheck();
	}

	/**
	 * registerOnChange
	 *
	 * Registers a callback function that should be called when the control's value changes in the UI.
	 *
	 * @param fn - The callback function
	 */
	public registerOnChange(fn: Function): void {
		this.onModelChange = fn;
	}

	/**
	 * registerOnTouched
	 *
	 * Registers a callback function that should be called when the control is touched.
	 *
	 * @param fn - The callback function
	 */
	public registerOnTouched(fn: Function): void {
		this.onTouch = fn;
	}

	/**
	 * setDisabledState
	 *
	 * Sets the disabled state of the control.
	 *
	 * @param isDisabled - The new disabled state
	 */
	public setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;

		this.cdRef.markForCheck();
	}

	/**
	 * onChange
	 *
	 * Handles the change event when the checkbox button is clicked.
	 *
	 * @param value - The new value for the checkbox button
	 */
	public onChange(value: boolean): void {
		this.isChecked = value;

		this.onModelChange(this.isChecked || null);
		this.onTouch();
	}

	/**
	 * onTouched
	 *
	 * Handles the touch event.
	 */
	public onTouched(): void {
		this.onTouch();
	}

	/**
	 * markForCheck
	 *
	 * Marks the component for change detection.
	 */
	public markForCheck(): void {
		this.cdRef.markForCheck();
	}

	/**
	 * onKeyDown
	 *
	 * Handles the keydown event for keyboard interactions.
	 * When the pressed key is Enter or Space, the checkbox button is toggled.
	 * This simulates the behaviour of a native checkbox.
	 *
	 * @param e - The keyboard event
	 */
	public onKeyDown(e: KeyboardEvent): void {
		if (e.key !== 'Enter' && e.key !== ' ') {
			return;
		}

		e.preventDefault();
		this.onChange(!this.isChecked);
	}
}
